import { Navigate, Route, Routes } from 'react-router-dom'
import { CashdeskPage } from '@/pages/cashdesk/CashdeskPage'
import { GrantedPlansPage } from '@/pages/GrantedPlansPage'
import { MyLoginsPage } from '@/pages/MyLoginsPage'
import { MyOperationsPage } from '@/pages/MyOperationsPage'
import { MyVouchersPage } from '@/pages/MyVouchersPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { ProfilePage } from '@/pages/ProfilePage'
import { SubscribersPage } from '@/pages/SubscribersPage'
import { VoucherPage } from '@/pages/VoucherPage'
import { LoginPage } from '@/pages/auth/LoginPage'
import { TenantChoicePage } from '@/pages/auth/TenantChoicePage'
import { TotpPage } from '@/pages/auth/TotpPage'
import { RequireAuth } from '@/shared/auth/RequireAuth'
import { RequirePermission } from '@/shared/auth/RequirePermission'
import { PERM } from '@/shared/auth/partnerPermissions'
import { useAuth } from '@/shared/auth/useAuth'
import { AppLayout } from './layout/AppLayout'

/**
 * BOSHLANG'ICH EKRAN SHERIK TURIDAN KELIB CHIQADI — ADR-030 §1.
 *
 * Kassa huquqi bo'lsa «Kassa», aks holda abonentlar ro'yxati. Ikkala
 * yo'nalish ham amaliy: agent kunini QIDIRUV MAYDONIDAN boshlaydi
 * (abonent qarshisida turibdi), reseller esa o'z shoxining ro'yxatidan.
 * Noto'g'ri ekran har kirishda bir bosish qo'shadi va u HAR SAFAR
 * to'lanadi.
 *
 * ⚠️ Tur SO'RALMAYDI — huquq so'raladi. `PartnerKind` to'plamlari
 * (ADR-029 §2) `subscriber.directory` ni faqat kassa oladigan sherikka
 * beradi, ya'ni «pul ham oladigan reseller» ham O'ZIDAN Kassaga
 * tushadi. `partnerKind === 'AGENT'` deb yozilsa aynan o'sha holat
 * chetda qolardi.
 */
function StartScreen() {
  const { can } = useAuth()
  return can(PERM.subscriberDirectory) ? <CashdeskPage /> : <Navigate to="/subscribers" replace />
}

/**
 * «Sheriklar kabineti» marshrutlari — ADR-030 §2.
 *
 * Ikki qatlamli himoya:
 *   `RequireAuth`       — kirilganmi (va `me` yuklanganmi)
 *   `RequirePermission` — shu sahifaga huquq bormi
 *
 * Ikkinchisi navigatsiyadagi filtrni (`useVisibleNav`) TAKRORLAYDI va
 * bu ataylab: menyuda havola bo'lmasa ham URL qo'lda kiritilishi yoki
 * eski havola saqlangan bo'lishi mumkin (04-ROLES.md §8).
 *
 * ⚠️ BOSH SAHIFA sherik turiga qarab tanlanadi — `StartScreen` izohi.
 *
 * ⚠️ Sahifalar `lazy` QILINMADI. Ilova kichkina (bitta haqiqiy ekran +
 * uchta o'rin egallovchi) va kassirning telefoni ko'pincha sekin
 * tarmoqda bo'ladi: bo'laklarga bo'lish har o'tishda YANGI so'rov
 * qo'shardi — aynan u yerda eng qimmat.
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* ⚠️ Sherik `/api/partner/login` orqali kiradi va admin login uni
          RAD ETADI (ADR-029 §4). `client.ts` dagi 401 ishlovchisi ham
          aynan shu yo'lga qaytaradi. */}
      <Route path="/login" element={<LoginPage />} />
      {/* Kirish oqimining oraliq bosqichlari — faqat navigatsiya holati
          bilan yetib kelinadi, to'g'ridan-to'g'ri ochilsa `/login` ga
          qaytaradi. TARTIB: parol → TOTP → (tashkilot tanlash). */}
      <Route path="/login/totp" element={<TotpPage />} />
      <Route path="/login/tenant" element={<TenantChoicePage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppLayout />}>
          <Route index element={<StartScreen />} />

          {/* Amallar jurnali va berilgan paketlar — `agent.read` bilan,
              ya'ni `GET /api/agents/{id}/operations` NIMA so'rasa,
              shuning O'ZI. Marshrut qo'riqchisi menyudagi filtrni
              TAKRORLAYDI va bu ataylab: menyuda havola bo'lmasa ham URL
              qo'lda kiritilishi mumkin (04-ROLES.md §8).

              ⚠️ Ilgari bu yerda `subscriber.directory` turardi — KASSA
              belgisi. Kassasiz reseller (`cash_desk = f`) ikkala
              ekrandan ham mahrum edi, holbuki server unga o'sha
              so'rovlarga `200` beradi (o'lchandi 2026-08-21). */}
          <Route element={<RequirePermission permission={PERM.agentRead} />}>
            <Route path="my-operations" element={<MyOperationsPage />} />
            {/* «Berilgan paketlar» — ayni huquq, ayni sabab: ro'yxat
                ayni endpointdan `kind=GRANT` bilan olinadi. */}
            <Route path="granted" element={<GrantedPlansPage />} />
          </Route>

          {/* Abonentlar — reseller uchun. Server javobni O'Z SHOXI bilan
              cheklaydi (SUBTREE doirasi, ADR-029 §2) — panel doirani
              takrorlamaydi, faqat havolani yashiradi. */}
          <Route element={<RequirePermission permission={PERM.subscriberRead} />}>
            <Route path="subscribers" element={<SubscribersPage />} />
          </Route>

          {/* Karta (vaucher) faollashtirish — `voucher.activate`, ya'ni
              endpointning O'Z huquqi (`VoucherController.activate`).

              ⛔ SHART SHERIK TURIGA QARAB YOZILMAYDI va kassa bayrog'iga
              ham bog'lanmaydi: huquq IKKALA `PartnerKind` to'plamining
              bazaviy qismida (`:55`, `:92`). `subscriber.directory`
              qo'yilsa kassasiz reseller sahifadan mahrum bo'lardi,
              holbuki server unga `200` beradi. */}
          <Route element={<RequirePermission permission={PERM.voucherActivate} />}>
            <Route path="voucher" element={<VoucherPage />} />
          </Route>

          {/* «Kartalarim» — O'Z partiyalari, ulardagi kodlar va o'zi
              ishlatgan kartalar (egasi talabi, 2026-08-24).

              Sharti `voucher.stock.read` — OMBOR huquqi, ya'ni
              `GET /api/vouchers/my/**` uchun mo'ljallangan kod.

              ⛔ `PERM.voucherActivate` QO'YILMAYDI va bu o'lchangan
              qaror: u AMAL huquqi va ikkala `PartnerKind` to'plamida
              bor, ya'ni sahifa HAMMAGA ochilardi. Kod esa bearer sir —
              kartani ishlata oladigan har kim boshqa sherikning
              kodlarini ko'radigan bo'lib qolardi.

              ⚠️ Huquq serverda BOR (`PartnerKind.java:63` va `:125`,
              o'lchandi 2026-08-24), doirasi esa turdan kelib chiqadi:
              AGENT `OWN`, RESELLER `SUBTREE`.

              ⚠️ `/voucher` (faollashtirish) TEGILMADI: u boshqa
              endpointning boshqa huquqi va boshqa savol. */}
          <Route element={<RequirePermission permission={PERM.voucherStockRead} />}>
            <Route path="vouchers" element={<MyVouchersPage />} />
          </Route>

          {/* Balans va komissiya — HAR sherikda bor, huquqsiz */}
          <Route path="profile" element={<ProfilePage />} />

          {/* «Mening kirishlarim» — HUQUQSIZ va bu serverdagi qaror
              bilan bir xil: `GET /api/auth/my-logins` huquq so'ramaydi,
              chunki bu hisobning O'Z ma'lumoti, va identifikatorni
              TOKENDAN oladi (`AuthResource#myLogins`). `audit.read` ni
              talab qilish butun audit jurnalini ochib yuborardi. */}
          <Route path="my-logins" element={<MyLoginsPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  )
}
